import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useNavigation } from '../../context/NavigationContext';
import { tournamentAPI } from '../../services/api';
import { Card, StatusBadge, ScoreDisplay, Ico, Icons, PlayerAvatar, AddToCalendar, resolveTeam, HomeSkeleton, TournamentCard, HomeMatchesPreview } from '../../components/common';
import { formatDateIST } from '../../utils/dateUtils';

export default function Home() {
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const { savePageState, getPageState } = useNavigation();
  const [ready, setReady] = useState(false);
  const [tournaments, setTournaments] = useState(() => getPageState('tournaments', []));
  const [topPlayers, setTopPlayers] = useState(() => getPageState('topPlayers', []));
  const [featuredType, setFeaturedType] = useState(() => getPageState('featuredType', 'featured'));
  const [stats, setStats] = useState(() => getPageState('stats', { totalTournaments: 0, totalPlayers: 0, liveMatches: 0, todayMatches: 0, completedMatches: 0 }));

  // Save state on changes
  useEffect(() => {
    if (tournaments.length > 0) savePageState('tournaments', tournaments);
  }, [tournaments, savePageState]);

  useEffect(() => {
    if (topPlayers.length > 0) savePageState('topPlayers', topPlayers);
  }, [topPlayers, savePageState]);

  useEffect(() => {
    savePageState('featuredType', featuredType);
  }, [featuredType, savePageState]);

  useEffect(() => {
    savePageState('stats', stats);
  }, [stats, savePageState]);

  useEffect(() => {
    const load = async () => {
      // If we have cached data, show it immediately
      const cachedTournaments = getPageState('tournaments', []);
      if (cachedTournaments.length > 0) {
        setReady(true);
      }
      
      try {
        const t0 = Date.now();
        const { data } = await tournamentAPI.getHomeSummary();
        
        setFeaturedType(data.featuredType || 'featured');
        setTournaments(data.tournaments || []);
        setTopPlayers(data.topPlayers || []);
        setStats(data.stats || {});
        setTimeout(() => setReady(true), Math.max(0, 20 - (Date.now() - t0)));
      } catch (e) {
        console.error('Failed to load home data:', e);
        setReady(true);
      }
    };
    load();
  }, [navigate, getPageState]);

  const featured = tournaments;

  if (!ready) return <HomeSkeleton />;

  return (
    <div className="container">
      {/* Hero CTA */}
      <section className="home-hero">
        <div className="home-hero-content home-hero-layout">
          <div className="home-hero-left">
            <h1 className="home-hero-title">Your Game, One Platform</h1>
            <p className="home-hero-sub">Real-time scores · Tournament brackets · Player rankings · Match schedules</p>
            <p className="home-hero-proof">Join thousands of players and organizers on TennisBuzz</p>
            {!currentUser ? (
              <div className="hero-cta-row">
                <Link to="/login" className="btn-primary-hero hero-cta-btn">Sign In / Register</Link>
                <Link to="/tournaments" className="btn-outline-hero hero-cta-btn">Browse Tournaments</Link>
              </div>
            ) : currentUser.role === 'organizer' ? (
              <div className="hero-cta-row">
                <Link to="/organizer" className="btn-primary-hero hero-cta-btn">My Events</Link>
                <Link to="/tournaments" className="btn-outline-hero hero-cta-btn">Browse Tournaments</Link>
              </div>
            ) : (
              <div className="hero-cta-row">
                <Link to="/tournaments" className="btn-outline-hero hero-cta-btn">Browse Tournaments</Link>
              </div>
            )}
          </div>
          <div className="home-hero-right">
            <div className="hero-stat-grid">
              <Link to="/matches" style={{ textDecoration: 'none' }}>
                <div className="hero-stat-item">
                  <div className="hero-stat-num">{stats.liveMatches}</div>
                  <div className="hero-stat-txt">Live Now</div>
                </div>
              </Link>
              <Link to="/tournaments" style={{ textDecoration: 'none' }}>
                <div className="hero-stat-item">
                  <div className="hero-stat-num">{stats.totalTournaments}</div>
                  <div className="hero-stat-txt">Tournaments</div>
                </div>
              </Link>
              <Link to="/players" style={{ textDecoration: 'none' }}>
                <div className="hero-stat-item">
                  <div className="hero-stat-num">{stats.totalPlayers}</div>
                  <div className="hero-stat-txt">Players</div>
                </div>
              </Link>
              <div className="hero-stat-item">
                <div className="hero-stat-num">{stats.completedMatches}</div>
                <div className="hero-stat-txt">Matches Played</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="home-quick-stats home-quick-stats-mobile">
        <div className="home-stat-strip">
          <Link to="/live" className="home-stat-chip">
            <span className="home-stat-val">{stats.liveMatches}</span>
            <span className="home-stat-lbl">Live</span>
          </Link>

          <div className="home-stat-div" />
          <Link to="/tournaments" className="home-stat-chip">
            <span className="home-stat-val">{stats.totalTournaments}</span>
            <span className="home-stat-lbl">Tourneys</span>
          </Link>
          <div className="home-stat-div" />
          <Link to="/players" className="home-stat-chip">
            <span className="home-stat-val">{stats.totalPlayers}</span>
            <span className="home-stat-lbl">Players</span>
          </Link>
        </div>
      </section>

      {/* Live Scores */}
      <HomeMatchesPreview />

      {/* Featured Tournaments */}
      <section style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 className="heading">{featuredType === 'completed' ? 'RECENTLY COMPLETED' : 'FEATURED TOURNAMENTS'}</h2>
          <Link to="/tournaments" style={{ fontSize: 12, color: '#1E88E5', fontWeight: 600, textDecoration: 'none' }}>View All →</Link>
        </div>
        {featured.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><Ico d={Icons.trophy} size={36} color="#B0B8C9" /></div>
            <h3 className="empty-title">No Tournaments Yet</h3>
            <p className="empty-sub">Featured tournaments will appear here</p>
          </div>
        ) : (
        <div className="grid-cards">
          {featured.map(t => <TournamentCard key={t.id} tournament={t} />)}
        </div>
        )}
      </section>

      {/* Top Players - desktop only */}
      <section className="home-desktop-only" style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 className="heading">TOP PLAYERS</h2>
          <Link to="/players" style={{ fontSize: 12, color: '#1E88E5', fontWeight: 600, textDecoration: 'none' }}>View All →</Link>
        </div>
        {topPlayers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><Ico d={Icons.users} size={36} color="#B0B8C9" /></div>
            <h3 className="empty-title">No Players Yet</h3>
            <p className="empty-sub">Top players will appear here</p>
          </div>
        ) : (
        <div className="grid-players-home">
          {topPlayers.slice(0, 5).map(p => {
            const wr = p.matchesPlayed ? ((p.wins / p.matchesPlayed) * 100).toFixed(0) : 0;
            return (
              <Link to={`/players/${p.id}`} key={p.id} style={{ textDecoration: 'none' }}>
                <div className="p-card">
                  <div className="p-top">
                    <PlayerAvatar name={p.name} photo={p.photo} size={34} />
                    <div style={{ flex: 1 }}>
                      <div className="p-name">{p.name}</div>
                      <div className="p-country">{p.country}</div>
                    </div>
                  </div>
                  <div className="p-stats">
                    <span style={{ color: '#43A047' }}>{p.wins}W</span>
                    <span className="p-dot">·</span>
                    <span style={{ color: '#E53935' }}>{p.losses}L</span>
                    <span className="p-dot">·</span>
                    <span style={{ color: '#FB8C00' }}>{p.titles}T</span>
                  </div>
                  <div className="bar bar-thin"><div className="bar-fill" style={{ width: `${wr}%`, background: '#00205B' }} /></div>
                </div>
              </Link>
            );
          })}
        </div>
        )}
      </section>

      {/* Browse links - mobile only */}
      <section className="home-mobile-only" style={{ marginBottom: 32 }}>
      </section>
    </div>
  );
}
